/**
 * Mapeo de códigos de error de Auth.js v5 (`?error=...`) a mensajes accionables
 * en español.
 *
 * Auth.js redirige a `pages.error` con un código en el query string cuando algo
 * falla en el flujo (token caducado, OAuth callback fallido, rate-limit, etc.).
 * Cada app renderiza el banner con su propia presentación visual, pero el texto
 * vive aquí para mantenerlo unificado y testeable.
 *
 * Los textos son intencionalmente neutros para no distinguir entre "no existe
 * el usuario" y otros casos (anti-enumeración, decisión 3.6 informe 09).
 *
 * Ref: `@auth/core/errors.js` (clientErrors set).
 */

export const AUTH_ERROR_MESSAGES: Record<string, string> = {
  Verification:
    "El código no es válido o ha caducado. Vuelve a intentarlo o pide uno nuevo.",
  AccessDenied:
    "No tienes acceso o has superado el número de intentos. Espera unos minutos e inténtalo de nuevo.",
  OAuthSignin:
    "Hubo un problema al iniciar sesión con Google. Inténtalo de nuevo.",
  OAuthCallback:
    "Google no pudo completar el login. Inténtalo de nuevo o usa el código por email.",
  OAuthCreateAccount:
    "No pudimos crear la cuenta tras el login con Google. Inténtalo de nuevo.",
  OAuthAccountNotLinked:
    "Este email ya está vinculado a otra forma de acceso. Usa el método con el que te registraste.",
  EmailCreateAccount:
    "No pudimos crear la cuenta para este email. Inténtalo de nuevo.",
  EmailSignin:
    "No pudimos enviar el código por email. Inténtalo de nuevo en unos minutos.",
  CredentialsSignin: "Las credenciales no son válidas. Inténtalo de nuevo.",
  Callback: "Hubo un problema al completar el login. Inténtalo de nuevo.",
  SessionRequired: "Necesitas iniciar sesión para acceder a esta página.",
  Configuration:
    "Hay un problema de configuración en el servidor. Avisa al administrador.",
  Default:
    "Hubo un problema al iniciar sesión. Inténtalo de nuevo en unos minutos.",
};

/**
 * Devuelve el mensaje accionable correspondiente al código de error de Auth.js,
 * o `undefined` cuando no hay código (no se renderiza banner). Cualquier código
 * desconocido cae en el mensaje `Default`.
 */
export function mapAuthError(code: string | null | undefined): string | undefined {
  if (code === null || code === undefined) return undefined;
  return AUTH_ERROR_MESSAGES[code] ?? AUTH_ERROR_MESSAGES.Default;
}
